/**
 * Users Settings Page
 *
 * Admin interface for managing organization users.
 * Features role-based filtering, status badges, and inline actions.
 */

import { useState } from 'react';
import {
  Users,
  Search,
  AlertCircle,
  AlertTriangle,
  Loader2,
  Pencil,
  UserX,
  UserCheck,
  ChevronDown,
  Clock,
  RefreshCw,
  Shield,
  X,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import {
  useUsers,
  useCurrentUser,
  useDeactivateUser,
  useReactivateUser,
  useAuth0SyncStatus,
  useAuth0SyncAudit,
  useResolveAuth0Conflict,
  useInvalidateAuth0Cache,
} from '../../hooks/useUsers';
import type { Auth0SyncConflict, Auth0SyncAudit } from '../../hooks/useUsers';
import { UserForm } from '../../components/users/UserForm';
import type { UserListItem, UserRole } from '@guild/shared';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';

// Role configuration with colors and labels
const roleConfig: Partial<Record<UserRole, { label: string; bg: string; text: string; dot: string }>> = {
  super_admin: {
    label: 'Super Admin',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    dot: 'bg-amber-500',
  },
  admin: {
    label: 'Admin',
    bg: 'bg-indigo-50',
    text: 'text-indigo-700',
    dot: 'bg-indigo-500',
  },
  manager: {
    label: 'Manager',
    bg: 'bg-violet-50',
    text: 'text-violet-700',
    dot: 'bg-violet-500',
  },
  trainer: {
    label: 'Trainer',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    dot: 'bg-blue-500',
  },
  operator: {
    label: 'Operator',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    dot: 'bg-emerald-500',
  },
};

// Fallback for any legacy roles not in roleConfig
const defaultRoleStyle = { label: 'Unknown', bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' };

const roleOptions: { value: UserRole | ''; label: string }[] = [
  { value: '', label: 'All Roles' },
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'trainer', label: 'Trainer' },
  { value: 'operator', label: 'Operator' },
];

export function UsersPage() {
  useDocumentTitle('Users');
  // Phase 19.S frontend migration — read role from DB-canonical /api/me, not the JWT claim.
  const { data: currentUser } = useCurrentUser();
  const userRole = currentUser?.role;
  const isSuperAdmin = userRole === 'super_admin';
  const isAdmin = userRole === 'admin' || isSuperAdmin;

  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<UserRole | ''>('');
  const [showInactive, setShowInactive] = useState(false);
  const [editingUser, setEditingUser] = useState<UserListItem | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [showSyncPanel, setShowSyncPanel] = useState(false);
  const [auditRequested, setAuditRequested] = useState(false);

  const { data: usersResponse, isLoading, error } = useUsers({
    search: searchQuery || undefined,
    role: roleFilter || undefined,
    isActive: showInactive ? undefined : true,
  });

  const deactivateMutation = useDeactivateUser();
  const reactivateMutation = useReactivateUser();

  // Auth0 sync hooks (super_admin only)
  const { data: syncStatus } = useAuth0SyncStatus(isSuperAdmin);
  const {
    data: syncAudit,
    isLoading: isAuditLoading,
    refetch: refetchAudit,
  } = useAuth0SyncAudit(isSuperAdmin && auditRequested);

  // Extract users array from paginated response
  const users = Array.isArray(usersResponse) ? usersResponse : usersResponse?.data ?? [];

  const handleEdit = (user: UserListItem) => {
    setEditingUser(user);
    setIsFormOpen(true);
  };

  const handleDeactivate = async (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to deactivate ${name}? They will no longer be able to sign in.`)) {
      await deactivateMutation.mutateAsync(id);
    }
  };

  const handleReactivate = async (id: string, name: string) => {
    if (window.confirm(`Reactivate ${name}? They will be able to sign in again.`)) {
      await reactivateMutation.mutateAsync(id);
    }
  };

  const handleRunAudit = () => {
    setAuditRequested(true);
    setShowSyncPanel(true);
    // If already fetched, refetch to get fresh data
    if (auditRequested) {
      refetchAudit();
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingUser(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-violet-50">
            <Users size={24} className="text-violet-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
              Users
            </h1>
            <p className="mt-0.5 text-sm text-gray-500">
              Manage team members and their access roles
            </p>
          </div>
        </div>
      </div>

      {/* Super Admin: Sync Status Banner */}
      {isSuperAdmin && syncStatus && (
        <SyncStatusBanner
          syncStatus={syncStatus}
          syncAudit={syncAudit}
          auditRequested={auditRequested}
          onRunAudit={handleRunAudit}
          onShowPanel={() => { setAuditRequested(true); setShowSyncPanel(true); }}
        />
      )}

      {/* Filters Bar */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Search */}
        <div className="relative flex-1 min-w-[280px]">
          <Search
            size={18}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="
              w-full rounded-xl border border-gray-200 bg-white
              pl-11 pr-4 py-2.5 text-sm
              placeholder:text-gray-400
              focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
              transition-all duration-200
            "
          />
        </div>

        {/* Role Filter */}
        <div className="relative">
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value as UserRole | '')}
            className="
              appearance-none rounded-xl border border-gray-200 bg-white
              pl-4 pr-10 py-2.5 text-sm font-medium text-gray-700
              focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
              cursor-pointer transition-all duration-200
            "
          >
            {roleOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <ChevronDown
            size={16}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
        </div>

        {/* Inactive filter */}
        {isAdmin && (
          <label className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl border border-gray-200 bg-white cursor-pointer hover:border-gray-300 transition-colors">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 bg-white text-guild-green focus:ring-guild-green focus:ring-offset-0"
            />
            <span className="text-sm font-medium text-gray-600">
              Show inactive
            </span>
          </label>
        )}
      </div>

      {/* Content */}
      {error ? (
        <ErrorState />
      ) : isLoading ? (
        <LoadingState />
      ) : users.length > 0 ? (
        <UserTable
          users={users}
          isAdmin={isAdmin}
          onEdit={handleEdit}
          onDeactivate={handleDeactivate}
          isDeactivating={deactivateMutation.isPending}
          onReactivate={handleReactivate}
          isReactivating={reactivateMutation.isPending}
        />
      ) : (
        <EmptyState searchQuery={searchQuery} roleFilter={roleFilter} />
      )}

      {/* Edit Form Modal */}
      {isFormOpen && editingUser && (
        <UserForm user={editingUser} onClose={handleFormClose} />
      )}

      {/* Super Admin: Sync Audit Panel */}
      {isSuperAdmin && showSyncPanel && (
        <SyncAuditPanel
          audit={syncAudit}
          isLoading={isAuditLoading}
          onClose={() => setShowSyncPanel(false)}
          onRefresh={() => refetchAudit()}
        />
      )}
    </div>
  );
}

// ============================================
// SUB-COMPONENTS
// ============================================

interface UserTableProps {
  users: UserListItem[];
  isAdmin: boolean;
  onEdit: (user: UserListItem) => void;
  onDeactivate: (id: string, name: string) => void;
  isDeactivating: boolean;
  onReactivate: (id: string, name: string) => void;
  isReactivating: boolean;
}

function UserTable({ users, isAdmin, onEdit, onDeactivate, isDeactivating, onReactivate, isReactivating }: UserTableProps) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50/50">
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              User
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Role
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Last Login
            </th>
            {isAdmin && (
              <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {users.map((user) => (
            <UserRow
              key={user.id}
              user={user}
              isAdmin={isAdmin}
              onEdit={() => onEdit(user)}
              onDeactivate={() => onDeactivate(user.id, user.fullName)}
              isDeactivating={isDeactivating}
              onReactivate={() => onReactivate(user.id, user.fullName)}
              isReactivating={isReactivating}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface UserRowProps {
  user: UserListItem;
  isAdmin: boolean;
  onEdit: () => void;
  onDeactivate: () => void;
  isDeactivating: boolean;
  onReactivate: () => void;
  isReactivating: boolean;
}

function UserRow({ user, isAdmin, onEdit, onDeactivate, isDeactivating, onReactivate, isReactivating }: UserRowProps) {
  const role = roleConfig[user.role as keyof typeof roleConfig] || defaultRoleStyle;
  const initials = user.fullName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const formatLastLogin = (date: Date | null) => {
    if (!date) return 'Never';
    const d = new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return d.toLocaleDateString();
  };

  return (
    <tr
      className={`
        group transition-colors
        ${user.isActive ? 'hover:bg-gray-50/50' : 'bg-gray-50/30'}
      `}
    >
      {/* User Info */}
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          {/* Avatar */}
          <div
            className={`
              flex h-10 w-10 items-center justify-center rounded-full
              font-semibold text-sm
              ${user.isActive
                ? 'bg-gradient-to-br from-guild-green to-guild-green/80 text-white'
                : 'bg-gray-200 text-gray-500'
              }
            `}
          >
            {initials}
          </div>
          <div className="min-w-0">
            <p
              className={`
                text-sm font-semibold truncate
                ${user.isActive ? 'text-gray-900' : 'text-gray-400'}
              `}
            >
              {user.fullName}
            </p>
            <p
              className={`
                text-xs truncate
                ${user.isActive ? 'text-gray-500' : 'text-gray-400'}
              `}
            >
              {user.email}
            </p>
          </div>
        </div>
      </td>

      {/* Role Badge */}
      <td className="px-6 py-4">
        <span
          className={`
            inline-flex items-center gap-1.5 rounded-full px-2.5 py-1
            text-xs font-semibold
            ${user.isActive ? `${role.bg} ${role.text}` : 'bg-gray-100 text-gray-400'}
          `}
        >
          <span
            className={`
              h-1.5 w-1.5 rounded-full
              ${user.isActive ? role.dot : 'bg-gray-300'}
            `}
          />
          {role.label}
        </span>
      </td>

      {/* Status */}
      <td className="px-6 py-4">
        <span
          className={`
            inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold
            ${user.isActive
              ? 'bg-emerald-50 text-emerald-700'
              : 'bg-gray-100 text-gray-500'
            }
          `}
        >
          <span
            className={`
              h-1.5 w-1.5 rounded-full
              ${user.isActive ? 'bg-emerald-500' : 'bg-gray-400'}
            `}
          />
          {user.isActive ? 'Active' : 'Inactive'}
        </span>
      </td>

      {/* Last Login */}
      <td className="px-6 py-4">
        <div className="flex items-center gap-2">
          <Clock
            size={14}
            className={user.isActive ? 'text-gray-400' : 'text-gray-300'}
          />
          <span
            className={`
              text-sm
              ${user.isActive ? 'text-gray-600' : 'text-gray-400'}
            `}
          >
            {formatLastLogin(user.lastLoginAt)}
          </span>
        </div>
      </td>

      {/* Actions */}
      {isAdmin && (
        <td className="px-6 py-4 text-right">
          <div className="flex items-center justify-end gap-1">
            <button
              onClick={onEdit}
              className="
                inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5
                text-sm font-medium text-gray-600
                hover:bg-gray-100 hover:text-gray-900
                transition-colors
              "
            >
              <Pencil size={14} />
              Edit
            </button>
            {user.isActive ? (
              <button
                onClick={onDeactivate}
                disabled={isDeactivating}
                className="
                  inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5
                  text-sm font-medium text-red-600
                  hover:bg-red-50 hover:text-red-700
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition-colors
                "
              >
                <UserX size={14} />
                Deactivate
              </button>
            ) : (
              <button
                onClick={onReactivate}
                disabled={isReactivating}
                className="
                  inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5
                  text-sm font-medium text-emerald-600
                  hover:bg-emerald-50 hover:text-emerald-700
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition-colors
                "
              >
                <UserCheck size={14} />
                Reactivate
              </button>
            )}
          </div>
        </td>
      )}
    </tr>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <Loader2 size={32} className="text-guild-green animate-spin" />
      <p className="mt-4 text-sm text-gray-500">Loading users...</p>
    </div>
  );
}

function ErrorState() {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
      <AlertCircle size={20} className="text-red-500 flex-shrink-0" />
      <div>
        <p className="text-sm font-medium text-red-800">Failed to load users</p>
        <p className="text-sm text-red-600">
          Please try refreshing the page or contact support if the issue persists.
        </p>
      </div>
    </div>
  );
}

interface EmptyStateProps {
  searchQuery: string;
  roleFilter: string;
}

function EmptyState({ searchQuery, roleFilter }: EmptyStateProps) {
  const hasFilters = !!searchQuery || !!roleFilter;

  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-50 mb-5">
          {hasFilters ? (
            <Search size={32} className="text-gray-300" />
          ) : (
            <Users size={32} className="text-gray-300" />
          )}
        </div>

        <h3 className="text-lg font-semibold text-gray-900">
          {hasFilters ? 'No users found' : 'No users yet'}
        </h3>

        <p className="mt-2 text-sm text-gray-500 max-w-sm">
          {hasFilters
            ? 'No users match your current filters. Try adjusting your search or role filter.'
            : 'Users will appear here once they accept their invitation.'}
        </p>
      </div>
    </div>
  );
}

// ============================================
// SYNC STATUS COMPONENTS (super_admin only)
// ============================================

interface SyncStatusBannerProps {
  syncStatus: { cacheSize: number; dbUsers: { total: number; active: number; inactive: number } };
  syncAudit: Auth0SyncAudit | undefined;
  auditRequested: boolean;
  onRunAudit: () => void;
  onShowPanel: () => void;
}

function SyncStatusBanner({ syncStatus, syncAudit, auditRequested, onRunAudit, onShowPanel }: SyncStatusBannerProps) {
  const hasConflicts = syncAudit && syncAudit.summary.conflicts > 0;
  const hasErrors = syncAudit && syncAudit.summary.errors > 0;

  // If audit has been run and found conflicts, show amber warning banner
  if (hasConflicts || hasErrors) {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
        <AlertTriangle size={20} className="text-amber-500 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-amber-800">
            {syncAudit!.summary.conflicts > 0 &&
              `${syncAudit!.summary.conflicts} sync conflict${syncAudit!.summary.conflicts !== 1 ? 's' : ''}`}
            {syncAudit!.summary.conflicts > 0 && syncAudit!.summary.errors > 0 && ' and '}
            {syncAudit!.summary.errors > 0 &&
              `${syncAudit!.summary.errors} error${syncAudit!.summary.errors !== 1 ? 's' : ''}`}
            {' '}detected between database and Auth0
          </p>
          <p className="text-xs text-amber-600 mt-0.5">
            Role or status mismatches may cause users to have incorrect access.
          </p>
        </div>
        <button
          onClick={onShowPanel}
          className="
            text-sm font-semibold text-amber-700 hover:text-amber-800
            whitespace-nowrap px-3 py-1.5 rounded-lg hover:bg-amber-100
            transition-colors
          "
        >
          Review
        </button>
      </div>
    );
  }

  // Default: informational banner with user counts and audit trigger
  return (
    <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
      <Shield size={20} className="text-gray-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-700">
          Auth0 Sync:
          <span className="ml-2 text-gray-500 font-normal">
            {syncStatus.dbUsers.active} active,{' '}
            {syncStatus.dbUsers.inactive} inactive
            {syncStatus.cacheSize > 0 && (
              <span> &middot; {syncStatus.cacheSize} cached</span>
            )}
          </span>
        </p>
        {auditRequested && syncAudit && syncAudit.summary.conflicts === 0 && syncAudit.summary.errors === 0 && (
          <p className="text-xs text-emerald-600 mt-0.5 flex items-center gap-1">
            <CheckCircle size={12} />
            All {syncAudit.summary.synced} users in sync with Auth0
          </p>
        )}
      </div>
      <button
        onClick={onRunAudit}
        className="
          inline-flex items-center gap-1.5
          text-sm font-semibold text-gray-600 hover:text-gray-800
          whitespace-nowrap px-3 py-1.5 rounded-lg hover:bg-gray-100
          transition-colors
        "
      >
        <RefreshCw size={14} />
        Run Audit
      </button>
    </div>
  );
}

interface SyncAuditPanelProps {
  audit: Auth0SyncAudit | undefined;
  isLoading: boolean;
  onClose: () => void;
  onRefresh: () => void;
}

function SyncAuditPanel({ audit, isLoading, onClose, onRefresh }: SyncAuditPanelProps) {
  const resolveMutation = useResolveAuth0Conflict();
  const invalidateCacheMutation = useInvalidateAuth0Cache();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative w-full max-w-2xl max-h-[80vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50">
              <Shield size={20} className="text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Auth0 Sync Audit</h2>
              <p className="text-xs text-gray-500">Compare database state with Auth0 tenant</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => invalidateCacheMutation.mutate()}
              disabled={invalidateCacheMutation.isPending}
              className="
                inline-flex items-center gap-1.5 px-3 py-1.5
                text-xs font-medium text-gray-600 rounded-lg
                border border-gray-200 hover:bg-gray-50
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors
              "
              title="Clear middleware cache"
            >
              {invalidateCacheMutation.isPending ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <RefreshCw size={12} />
              )}
              Clear Cache
            </button>
            <button
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 size={28} className="text-guild-green animate-spin" />
              <p className="mt-3 text-sm text-gray-500">
                Running audit... This may take a moment for large teams.
              </p>
            </div>
          ) : audit ? (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-4 gap-3">
                <SummaryCard label="Total" value={audit.summary.total} color="gray" />
                <SummaryCard label="Synced" value={audit.summary.synced} color="emerald" />
                <SummaryCard label="Conflicts" value={audit.summary.conflicts} color="amber" />
                <SummaryCard label="Errors" value={audit.summary.errors} color="red" />
              </div>

              {/* Conflicts */}
              {audit.roleConflicts.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">
                    Role / Status Conflicts
                  </h3>
                  <div className="space-y-2">
                    {audit.roleConflicts.map((conflict) => (
                      <ConflictRow
                        key={conflict.dbId}
                        conflict={conflict}
                        onResolve={() => resolveMutation.mutate(conflict.dbId)}
                        isResolving={resolveMutation.isPending && resolveMutation.variables === conflict.dbId}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Auth0 Errors */}
              {audit.auth0Errors.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">
                    Auth0 API Errors
                  </h3>
                  <div className="space-y-2">
                    {audit.auth0Errors.map((err) => (
                      <div
                        key={err.dbId}
                        className="flex items-center gap-3 rounded-xl border border-red-100 bg-red-50 p-3"
                      >
                        <XCircle size={16} className="text-red-500 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-red-800 truncate">
                            {err.email}
                          </p>
                          <p className="text-xs text-red-600 truncate">{err.error}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* All synced message */}
              {audit.roleConflicts.length === 0 && audit.auth0Errors.length === 0 && (
                <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                  <CheckCircle size={20} className="text-emerald-500 flex-shrink-0" />
                  <p className="text-sm font-medium text-emerald-800">
                    All {audit.summary.synced} users are in sync with Auth0. No conflicts found.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Shield size={32} className="text-gray-300 mb-3" />
              <p className="text-sm text-gray-500">
                Click "Run Audit" to compare database and Auth0 state.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={onRefresh}
            disabled={isLoading}
            className="
              inline-flex items-center gap-1.5 px-4 py-2
              text-sm font-semibold text-gray-600
              rounded-xl hover:bg-gray-100
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-colors
            "
          >
            <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            onClick={onClose}
            className="
              px-4 py-2 text-sm font-semibold text-white bg-guild-green
              rounded-xl shadow-lg shadow-guild-green/20
              hover:bg-guild-green/95
              transition-all duration-200
            "
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, color }: { label: string; value: number; color: string }) {
  const colorMap: Record<string, string> = {
    gray: 'bg-gray-50 text-gray-700 border-gray-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    red: 'bg-red-50 text-red-700 border-red-200',
  };
  const classes = colorMap[color] || colorMap.gray;

  return (
    <div className={`rounded-xl border p-3 text-center ${classes}`}>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs font-medium mt-0.5">{label}</p>
    </div>
  );
}

function ConflictRow({
  conflict,
  onResolve,
  isResolving,
}: {
  conflict: Auth0SyncConflict;
  onResolve: () => void;
  isResolving: boolean;
}) {
  const roleConflict = conflict.dbRole !== conflict.auth0Role;
  // A status conflict exists when the blocked flag and active flag agree
  // (both true or both false), meaning Auth0 and DB are out of sync.
  // e.g. auth0Blocked=true + dbActive=true → should be blocked but isn't in DB
  const statusConflict =
    conflict.auth0Blocked !== undefined &&
    conflict.auth0Blocked === conflict.dbActive;

  return (
    <div className="flex items-center gap-3 rounded-xl border border-amber-100 bg-amber-50/50 p-3">
      <AlertTriangle size={16} className="text-amber-500 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900 truncate">
          {conflict.email}
        </p>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
          {roleConflict && (
            <p className="text-xs text-amber-700">
              Role: DB={conflict.dbRole}, Auth0={conflict.auth0Role ?? 'none'}
            </p>
          )}
          {statusConflict && (
            <p className="text-xs text-amber-700">
              Status: DB={conflict.dbActive ? 'active' : 'inactive'},
              Auth0={conflict.auth0Blocked ? 'blocked' : 'active'}
            </p>
          )}
        </div>
      </div>
      <button
        onClick={onResolve}
        disabled={isResolving}
        className="
          inline-flex items-center gap-1.5 px-3 py-1.5
          text-xs font-semibold text-amber-700
          rounded-lg border border-amber-200 bg-white
          hover:bg-amber-100
          disabled:opacity-50 disabled:cursor-not-allowed
          transition-colors whitespace-nowrap
        "
        title="Push DB state to Auth0"
      >
        {isResolving ? (
          <Loader2 size={12} className="animate-spin" />
        ) : (
          <RefreshCw size={12} />
        )}
        Resolve
      </button>
    </div>
  );
}
